import { AlertCircle, AlertTriangle,Info } from 'lucide-react';
import React, { JSX, useEffect, useState } from 'react';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface LogEntry {
  type: 'log' | 'error' | 'warn' | 'info';
  timestamp: string;
  message: string;
}

type ConsoleMethod = (...args: any[]) => void;

interface OriginalConsole {
  log: ConsoleMethod;
  error: ConsoleMethod;
  warn: ConsoleMethod;
  info: ConsoleMethod;
}

const ConsoleLogger: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const originalConsole: OriginalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info
    };

    console.log = (...args: any[]): void => {
      originalConsole.log.apply(console, args);
      addLog('log', args);
    };

    console.error = (...args: any[]): void => {
      originalConsole.error.apply(console, args);
      addLog('error', args);
    };

    console.warn = (...args: any[]): void => {
      originalConsole.warn.apply(console, args);
      addLog('warn', args);
    };

    console.info = (...args: any[]): void => {
      originalConsole.info.apply(console, args);
      addLog('info', args);
    };

    window.onerror = (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error
    ): void => {
      addLog('error', [`${message} at ${source}:${lineno}:${colno}`, error]);
    };

    window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
      addLog('error', ['Unhandled Promise Rejection:', event.reason]);
    };

    return () => {
      console.log = originalConsole.log;
      console.error = originalConsole.error;
      console.warn = originalConsole.warn;
      console.info = originalConsole.info;
      window.onerror = null;
      window.onunhandledrejection = null;
    };
  }, []);

  const addLog = (type: LogEntry['type'], args: any[]): void => {
    const log: LogEntry = {
      type,
      timestamp: new Date().toISOString(),
      message: args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ')
    };

    setLogs(prevLogs => [log, ...prevLogs]);
  };

  const getIcon = (type: LogEntry['type']): JSX.Element => {
    switch (type) {
      case 'error':
        return <AlertCircle className="text-red-500" size={16} />;
      case 'warn':
        return <AlertTriangle className="text-yellow-500" size={16} />;
      case 'info':
        return <Info className="text-blue-500" size={16} />;
      default:
        return <Info className="text-gray-500" size={16} />;
    }
  };

  const clearLogs = (): void => {
    setLogs([]);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="fixed bottom-4 right-4 bg-gray-800 text-white p-2 rounded-full shadow-lg hover:bg-gray-700 transition-colors">
          <AlertCircle size={24} />
        </button>
      </SheetTrigger>
      <SheetContent className="w-96">
        <SheetHeader className="space-y-4">
          <SheetTitle className="flex items-center gap-2">
            <button
                onClick={clearLogs}
                className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded transition-colors"
            >
              Clear
            </button>
            Console Logger
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 overflow-y-auto max-h-[calc(100vh-8rem)]">
          {logs.map((log, index) => (
              <div
                  key={index}
                  className={`mb-2 p-2 rounded flex items-start gap-2 ${
                log.type === 'error' ? 'bg-red-100 dark:bg-red-900/20' :
                log.type === 'warn' ? 'bg-yellow-100 dark:bg-yellow-900/20' :
                'bg-gray-100 dark:bg-gray-800'
              }`}
            >
              {getIcon(log.type)}
              <div className="flex-1 overflow-x-auto">
                <div className="text-xs text-gray-500 mb-1">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </div>
                <pre className="whitespace-pre-wrap break-words text-sm">
                  {log.message}
                </pre>
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ConsoleLogger;